
// src/services/api.ts
import { Capacitor } from '@capacitor/core';

const DB_KEY = 'amigo_peludo_db';

const convertToWebP = (file: File): Promise<Blob | File> => {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type.includes('svg')) {
    return Promise.resolve(file);
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1200;
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(file);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob || file), 'image/webp', 0.8);
      };
      img.onerror = () => resolve(file);
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

let isGuestMode = false;
let memoryDb: any = null;

export const setGuestMode = (mode: boolean) => {
  isGuestMode = mode;
  if (mode) {
    const eightMonthsAgo = new Date();
    eightMonthsAgo.setMonth(eightMonthsAgo.getMonth() - 8);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    memoryDb = {
      users: [],
      pets: [{
        id: 'demo-max', userId: 'guest', name: 'MAX', species: 'Perro', breed: 'Mestizo', gender: 'Macho',
        birthDate: eightMonthsAgo.toISOString().split('T')[0],
        imageUrl: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&q=80&w=400',
        color: 'Negro', weight: 15, conditions: '', treatments: '', allergies: '', medicalImportance: '',
        food_brand: 'Dog Chow', food_frequency: '2 veces al día', favorite_reward: 'Salchichas', disliked_food: '',
        personality_tags: 'Juguetón,Curioso', favorite_human: 'Visitante', fears: 'Aspiradora', mischief: 'Morder zapatos',
        likes: 'Pelotas', dislikes: 'Lluvia', favorite_toy: 'Hueso de goma', energy_level: 5, chip_number: '',
        tracker_url: '', has_gps: false, dominant_color: 'Negro', markings: 'Pecho blanco'
      }],
      vaccines: [
        { id: 'v-max-1', pet_id: 'demo-max', name: 'Sextuple/Óctuple (1ra Dosis)', date_applied: sixMonthsAgo.toISOString().split('T')[0], place: 'Clínica Demo', attachment: '', attachment_name: '' }
      ],
      healthRecords: [
        { id: 'hr-max-1', petId: 'demo-max', type: 'Control', date: sixMonthsAgo.toISOString().split('T')[0], clinic: 'Clínica Demo', doctor: 'Dr. Vet', diagnosis: 'Cachorro sano, desarrollo óptimo.', recommendation: 'Continuar con plan de vacunación', attachments: [] }
      ],
      medications: [],
      growthRecords: [],
      skills: [],
      posts: []
    };
  } else {
    memoryDb = null;
  }
};

const getDb = () => {
  if (isGuestMode && memoryDb) return JSON.parse(JSON.stringify(memoryDb)); // Return a deep copy so internal mutation doesn't break memory

  const dbStr = localStorage.getItem(DB_KEY);

  // 1. Initial Seed if DB doesn't exist
  if (!dbStr) {
    const now = new Date();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const in10Mins = new Date(now); in10Mins.setMinutes(now.getMinutes() + 5);

    const initialDb = {
      users: [
        {
          id: 'user-demo-1',
          name: 'Sophia Herrera',
          email: 'sophia@example.com',
          password: '123', // Demo password
          phone: '+56 9 1234 5678',
          rut: '12.345.678-9',
          region: 'Metropolitana',
          city: 'Providencia',
          address: 'Av. Providencia 1234 (Depto 402, Torre B)',
          referral_code: 'SOPHIA2024',
          points: 1250,
          wallet_balance: 12400,
          imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=400',
          role: 'client',
          is_public_wall: false
        }
      ],
      pets: [
        {
          id: 'demo-1', userId: 'sophia@example.com', name: 'TOBY', species: 'Perro', breed: 'Golden Retriever', gender: 'Macho',
          birthDate: '2023-09-15', imageUrl: 'https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80&w=200',
          color: 'Dorado', weight: 28, conditions: '', treatments: '', allergies: 'Pollo', medicalImportance: 'Alergia al Pollo',
          food_brand: 'Pro Plan', food_frequency: '2 veces al día', favorite_reward: 'Galletas', disliked_food: 'Verduras',
          personality_tags: 'Juguetón,Glotón', favorite_human: 'Sophia', fears: 'Ruidos fuertes', mischief: 'Romper cojines',
          likes: 'Correr', dislikes: 'Bañarse', favorite_toy: 'Pelota de tenis', energy_level: 5, chip_number: '98102030405060',
          tracker_url: '', has_gps: false, dominant_color: 'Dorado', markings: 'Mancha blanca en pecho'
        },
        {
          id: 'demo-2', userId: 'sophia@example.com', name: 'LUNA', species: 'Gato', breed: 'Siamés', gender: 'Hembra',
          birthDate: '2025-08-20', imageUrl: 'https://images.unsplash.com/photo-1513245543132-31f507417b26?auto=format&fit=crop&q=80&w=200',
          color: 'Siamés', weight: 4.5, conditions: '', treatments: '', allergies: '', medicalImportance: '',
          food_brand: 'Royal Canin', food_frequency: 'Libre demanda', favorite_reward: 'Churu', disliked_food: '',
          personality_tags: 'Independiente,Cariñosa', favorite_human: 'Mamá', fears: 'Aspiradora', mischief: 'Rasguñar sofá',
          likes: 'Dormir al sol', dislikes: 'Agua', favorite_toy: 'Ratón de juguete', energy_level: 2, chip_number: '',
          tracker_url: '', has_gps: false, dominant_color: 'Beige', markings: 'Puntos oscuros'
        }
      ],
      vaccines: [
        { id: 'vac-1', pet_id: 'demo-1', name: 'Séxtuple', date_applied: '2023-11-15', place: 'Vet City', attachment: '', attachment_name: '' },
        { id: 'vac-2', pet_id: 'demo-1', name: 'Antirrábica', date_applied: '2024-03-20', place: 'Vet City', attachment: '', attachment_name: '' },
        { id: 'vac-3', pet_id: 'demo-2', name: 'Triple Felina', date_applied: '2025-10-10', place: 'Clínica Gatos', attachment: '', attachment_name: '' }
      ],
      healthRecords: [],
      medications: [
        {
          id: 'med-demo-1',
          petId: 'demo-1',
          name: 'Apoquel 16mg',
          startDate: yesterday.toISOString().split('T')[0],
          timeStart: '08:00',
          frequencyHours: 24,
          duration: '30',
          remainingDoses: 28,
          nextDose: in10Mins.toISOString(), // ALERT!
          status: 'active'
        }
      ],
      posts: [
        {
          id: 'demo-post-1', pet_id: 'demo-1', pet_name: 'TOBY', pet_image: 'https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80&w=200', pet_gender: 'Macho',
          content: '¡Disfrutando del parque hoy! 🌳☀️', image_url: 'https://images.unsplash.com/photo-1534361960057-19889db9621e?auto=format&fit=crop&q=80&w=800', sticker: 'sun',
          created_at: new Date(Date.now() - 3600000).toISOString(), likes_count: 12, taggedUsers: []
        },
        {
          id: 'demo-post-2', pet_id: 'demo-2', pet_name: 'LUNA', pet_image: 'https://images.unsplash.com/photo-1513245543132-31f507417b26?auto=format&fit=crop&q=80&w=200', pet_gender: 'Hembra',
          content: 'Hora de la siesta... 💤', image_url: 'https://images.unsplash.com/photo-1511044568932-338cba0fb803?auto=format&fit=crop&q=80&w=800', sticker: 'moon',
          created_at: new Date(Date.now() - 86400000).toISOString(), likes_count: 34, taggedUsers: []
        }
      ],
      growthRecords: [
        { id: 'g1', petId: 'demo-1', date: '2023-11-15', weight: 12.5, height: 40, note: 'Creciendo rápido!' },
        { id: 'g2', petId: 'demo-1', date: '2024-05-15', weight: 24.0, height: 55, note: 'Ya es todo un adulto' }
      ],
      skills: [
        { id: 's1', petId: 'demo-1', name: 'Sentarse', date: '2023-10-01' },
        { id: 's2', petId: 'demo-1', name: 'Dar la pata', date: '2023-12-01' }
      ]
    };
    return initialDb;
  }

  // 2. Parse existing DB
  const db = JSON.parse(dbStr);

  // 3. Ensure Demo User always has data (if logging in again on unclean DB)
  if (!db.users) db.users = [];
  if (!db.users.some((u: any) => u.email === 'sophia@example.com')) {
    db.users.push({
      id: 'user-demo-1',
      name: 'Sophia Herrera',
      email: 'sophia@example.com',
      password: '123',
      phone: '+56 9 1234 5678',
      rut: '12.345.678-9',
      region: 'Metropolitana',
      city: 'Providencia',
      address: 'Av. Providencia 1234 (Depto 402, Torre B)',
      referral_code: 'SOPHIA2024',
      points: 1250,
      wallet_balance: 12400,
      imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=400',
      role: 'client'
    });
    localStorage.setItem(DB_KEY, JSON.stringify(db));
  }

  return db;
};

const saveDb = (data: any) => {
  if (isGuestMode) {
    memoryDb = JSON.parse(JSON.stringify(data)); // Deep copy to isolate
    return;
  }
  localStorage.setItem(DB_KEY, JSON.stringify(data));
};

const apiMock = {
  // --- AUTH ---
  login: async (email: string, password: string) => {
    const db = getDb();
    const user = db.users.find((u: any) => u.email === email && u.password === password);

    // Delay simulado
    await new Promise(resolve => setTimeout(resolve, 800));

    if (user) {
      return { message: 'EXITO', user };
    } else {
      // Fallback para demo pruskaminu si no está en users explícitamente pero está en código legacy
      if (email === 'pruskaminu@gmail.com') return { message: 'EXITO', user: { name: 'Sophia (Legacy)', email } };
      return { error: 'Credenciales inválidas' };
    }
  },

  register: async (userData: any) => {
    const db = getDb();
    // Check duplicate
    if (db.users.some((u: any) => u.email === userData.email)) {
      return { error: 'El correo ya está registrado' };
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    // Handle Referral
    let bonusPoints = 0;
    if (userData.referralCode) {
      // Logic: Check if referral code exists
      const referrer = db.users.find((u: any) => u.referral_code === userData.referralCode);
      if (referrer) {
        bonusPoints = 500;
        // Award points to referrer too?
        referrer.points = (referrer.points || 0) + 500;
        // Here we could track the referral in a separate table/collection if we wanted
      }
    }

    const generatedReferralCode = userData.name.split(' ')[0].toUpperCase() + new Date().getFullYear();

    const newUser = {
      ...userData,
      id: Date.now().toString(),
      points: 100 + bonusPoints, // Welcome bonus + referral
      wallet_balance: 0,
      referral_code: generatedReferralCode,
      role: 'client',
      imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba'
    };

    db.users.push(newUser);
    saveDb(db);

    return { message: 'EXITO', user: newUser };
  },

  // --- PETS ---
  getPets: async (email: string) => {
    const db = getDb();
    // Support matching by plain email or userId field
    return db.pets.filter((p: any) => p.userId === email);
  },

  searchPets: async (query: string) => {
    const db = getDb();
    if (!query) return db.pets;
    return db.pets.filter((p: any) => p.name.toLowerCase().includes(query.toLowerCase()));
  },

  addPet: async (data: any) => {
    const db = getDb();
    const newPet = { ...data, id: Date.now().toString() };
    db.pets.push(newPet);
    saveDb(db);
    return { ...newPet, message: 'EXITO' };
  },

  updatePet: async (id: string, data: any) => {
    const db = getDb();
    const index = db.pets.findIndex((p: any) => p.id === id);
    if (index !== -1) {
      db.pets[index] = { ...db.pets[index], ...data };
      saveDb(db);
      return db.pets[index];
    }
    throw new Error('Pet not found');
  },

  deletePet: async (id: string) => {
    const db = getDb();
    db.pets = db.pets.filter((p: any) => p.id !== id);
    saveDb(db);
  },

  transferPet: async (id: string, newEmail: string) => {
    const db = getDb();
    const index = db.pets.findIndex((p: any) => p.id === id);
    if (index !== -1) {
      db.pets[index].userId = newEmail;
      saveDb(db);
    }
  },

  getHealthRecords: async (petId: string) => {
    const db = getDb();
    return db.healthRecords.filter((r: any) => r.petId === petId);
  },

  addHealthRecord: async (data: any) => {
    const db = getDb();
    const newRecord = { ...data, id: Date.now().toString() };
    db.healthRecords.push(newRecord);
    saveDb(db);
    return newRecord;
  },

  updateHealthRecord: async (id: string, data: any) => {
    const db = getDb();
    const index = db.healthRecords.findIndex((r: any) => r.id === id);
    if (index !== -1) {
      db.healthRecords[index] = { ...db.healthRecords[index], ...data };
      saveDb(db);
      return db.healthRecords[index];
    }
  },

  deleteHealthRecord: async (id: string) => {
    const db = getDb();
    db.healthRecords = db.healthRecords.filter((r: any) => r.id !== id);
    saveDb(db);
  },

  getVaccines: async (petId: string) => {
    const db = getDb();
    return db.vaccines.filter((v: any) => v.pet_id === petId);
  },

  addVaccine: async (data: any) => {
    const db = getDb();
    const newVaccine = { ...data, id: Date.now().toString() };
    db.vaccines.push(newVaccine);
    saveDb(db);
    return newVaccine;
  },

  updateVaccine: async (id: string, data: any) => {
    const db = getDb();
    const index = db.vaccines.findIndex((v: any) => v.id === id);
    if (index !== -1) {
      db.vaccines[index] = { ...db.vaccines[index], ...data };
      saveDb(db);
      return db.vaccines[index];
    }
  },

  deleteVaccine: async (id: string) => {
    const db = getDb();
    db.vaccines = db.vaccines.filter((v: any) => v.id !== id);
    saveDb(db);
  },

  uploadImage: async (file: File) => {
    return new Promise<{ url: string }>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ url: reader.result as string });
      };
      reader.readAsDataURL(file);
    });
  },

  // --- MEDICATIONS ---
  getMedications: async (petId: string) => {
    const db = getDb();
    return (db.medications || []).filter((m: any) => m.petId === petId);
  },

  addMedication: async (data: any) => {
    const db = getDb();
    if (!db.medications) db.medications = [];
    const newMed = { ...data, id: Date.now().toString(), status: 'active' };
    db.medications.push(newMed);
    saveDb(db);
    return newMed;
  },

  updateMedication: async (id: string, data: any) => {
    const db = getDb();
    if (!db.medications) return;
    const index = db.medications.findIndex((m: any) => m.id === id);
    if (index !== -1) {
      db.medications[index] = { ...db.medications[index], ...data };
      saveDb(db);
      return db.medications[index];
    }
  },

  deleteMedication: async (id: string) => {
    const db = getDb();
    if (!db.medications) return;
    db.medications = db.medications.filter((m: any) => m.id !== id);
    saveDb(db);
  },

  getAllMedications: async (email: string) => {
    const db = getDb();
    // Get all pets for user
    const userPets = db.pets.filter((p: any) => p.userId === email);
    const petIds = userPets.map((p: any) => p.id);
    return (db.medications || []).filter((m: any) => petIds.includes(m.petId));
  },

  // --- UPLOAD ---


  // --- SOCIAL FEED ---
  getPosts: async () => {
    const db = getDb();
    return (db.posts || []).sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  },

  createPost: async (postData: any) => {
    const db = getDb();
    if (!db.posts) db.posts = [];
    const newPost = {
      ...postData,
      id: Date.now().toString(),
      created_at: new Date().toISOString(),
      likes_count: 0,
      taggedUsers: []
    };
    db.posts.unshift(newPost); // Add to beginning
    saveDb(db);
    return newPost;
  },

  likePost: async (postId: string) => {
    const db = getDb();
    const post = db.posts.find((p: any) => p.id === postId);
    if (post) {
      post.likes_count = (parseInt(post.likes_count) || 0) + 1;
      saveDb(db);
    }
  },

  tagPost: async (postId: string, tags: any[]) => {
    const db = getDb();
    const post = db.posts.find((p: any) => p.id === postId);
    if (post) {
      post.taggedUsers = tags;
      saveDb(db);
    }
  },

  commentPost: async (postId: string, comment: any) => {
    const db = getDb();
    const post = db.posts.find((p: any) => p.id === postId);
    if (post) {
      if (!post.comments) post.comments = [];
      post.comments.push(comment);
      saveDb(db);
    }
  },

  // --- ALERTS ---
  getActiveAlerts: async () => {
    const db = getDb();
    return { success: true, alerts: (db.alerts || []).filter((a: any) => a.status !== 'resolved') };
  },
  getFilteredAlerts: async (status: string, geoParams: any = {}) => {
    const db = getDb();
    let filtered = (db.alerts || []).filter((a: any) => status === 'active' ? a.status !== 'resolved' : a.status === 'resolved');
    
    if (geoParams.region) filtered = filtered.filter((a: any) => a.region === geoParams.region);
    if (geoParams.city) filtered = filtered.filter((a: any) => a.city?.toLowerCase().includes(geoParams.city.toLowerCase()));
    
    return { success: true, alerts: filtered };
  },

  createLostPetAlert: async (data: any) => {
    const db = getDb();
    if (!db.alerts) db.alerts = [];
    const newAlert = { 
      ...data, 
      id: Date.now().toString(), 
      created_at: new Date().toISOString(), 
      status: 'active' 
    };
    db.alerts.unshift(newAlert);
    saveDb(db);
    return { success: true, alert: newAlert };
  },

  resolveAlert: async (alertId: string, messageOrUserId?: string, user_id?: string, finderEmail?: string) => {
    const db = getDb();
    const alert = db.alerts.find((a: any) => a.id === alertId);
    if (alert) {
      alert.status = 'resolved';
      alert.resolved_at = new Date().toISOString();
      // If 4 args, likely called from CommunityAlertsView (Legacy)
      if (finderEmail) {
        alert.success_message = `¡Gracias a la comunidad (finder: ${finderEmail}) por ayudarme!`;
      } else {
        alert.success_message = messageOrUserId || '¡Gracias a la comunidad por ayudarme a volver a casa!';
      }
      saveDb(db);
    }
    return { success: true };
  },

  getResolvedAlerts: async () => {
    const db = getDb();
    return { success: true, alerts: (db.alerts || []).filter((a: any) => a.status === 'resolved') };
  },

  getAlertStats: async () => {
    const db = getDb();
    return { success: true, count: (db.alerts || []).length };
  },

  // --- SIGHTINGS ---
  createSighting: async (data: any) => {
    const db = getDb();
    if (!db.sightings) db.sightings = [];
    const newSighting = {
      ...data,
      id: Date.now().toString(),
      created_at: new Date().toISOString()
    };
    db.sightings.push(newSighting);
    saveDb(db);

    // Simulate notification event
    const alert = db.alerts?.find((a: any) => a.id === data.alert_id);
    if (alert) {
      window.dispatchEvent(new CustomEvent('system-alert', {
        detail: {
          level: '5',
          pet_id: alert.id,
          pet_name: alert.pet_name,
          pet_photo: alert.pet_image,
          body: `¡Nuevo avistamiento de ${alert.pet_name}! Alguien dice: "${data.details}"`,
          lat: data.lat,
          lng: data.lng,
          type: 'new_sighting'
        }
      }));
    }

    return { success: true, sighting: newSighting };
  },

  getSightings: async (alertId: string) => {
    const db = getDb();
    return { success: true, sightings: (db.sightings || []).filter((s: any) => String(s.alert_id) === String(alertId)) };
  },

  getNotifications: async () => {
    const db = getDb();
    return { success: true, notifications: db.notifications || [] };
  },
  markNotificationRead: async (id?: string | number) => {
    const db = getDb();
    if (id) {
      const n = (db.notifications || []).find((n: any) => n.id === id);
      if (n) n.is_read = 1;
    } else {
      (db.notifications || []).forEach((n: any) => n.is_read = 1);
    }
    saveDb(db);
    return { success: true };
  },

  // --- GROWTH RECORDS ---
  getPetGallery: async (petId: string) => {
    const db = getDb();
    return (db.petGallery || []).filter((g: any) => g.petId === petId);
  },

  addPetGalleryImage: async (data: { petId: string; url: string }) => {
    const db = getDb();
    if (!db.petGallery) db.petGallery = [];
    if (db.petGallery.filter((g: any) => g.petId === data.petId).length >= 10) {
      throw new Error('Máximo 10 imágenes permitidas');
    }
    const newDoc = { id: Date.now().toString(), petId: data.petId, url: data.url };
    db.petGallery.push(newDoc);
    saveDb(db);
    return newDoc;
  },

  deletePetGalleryImage: async (id: string | number) => {
    const db = getDb();
    if (db.petGallery) {
      db.petGallery = db.petGallery.filter((g: any) => String(g.id) !== String(id));
      saveDb(db);
    }
    return { success: true };
  },

  // --- SKILLS ---
  getSkills: async (petId: string) => {
    const db = getDb();
    return (db.skills || []).filter((s: any) => s.petId === petId);
  },

  addSkill: async (data: any) => {
    const db = getDb();
    if (!db.skills) db.skills = [];
    const newSkill = { ...data, id: Date.now().toString() };
    db.skills.push(newSkill);
    saveDb(db);
    return newSkill;
  },

  // --- CATEGORIES ---
  getCategories: async () => {
    return { success: true, data: [] };
  },


  // --- ADMIN MOCK ---
  getAdminStats: async (adminEmail: string) => {
    return {
      stats: [
        { label: 'Usuarios (Mock)', value: '1,240', change: '+12%', icon: 'Users', color: 'text-sky-500', bg: 'bg-sky-100' },
        { label: 'Ingresos (Mock)', value: '$4.2M', change: '+8.5%', icon: 'DollarSign', color: 'text-emerald-500', bg: 'bg-emerald-100' },
      ],
      ai_insights: ["Mock Insight 1", "Mock Insight 2"]
    };
  },

  // --- BREEDER MOCK ---
  breeder: {
    checkStatus: async (userId: string) => ({ isBreeder: true, breeder: { kennel_name: 'Mock Kennel' } }),
    getInventory: async (userId: string) => ({ puppies: [] }),
    createTransfer: async (data: any) => ({ success: true, token: 'mock-token-123' }),
    getValidationRequests: async () => {
      const db = getDb();
      return db.pedigreeRequests || [];
    },
    submitValidationRequest: async (data: any) => {
      const db = getDb();
      if (!db.pedigreeRequests) db.pedigreeRequests = [];
      const newReq = { ...data, id: Date.now(), status: 'pending', created_at: new Date().toISOString() };
      db.pedigreeRequests.push(newReq);
      saveDb(db);
      return { success: true, data: newReq };
    },
    updateValidationStatus: async (id: number, status: 'approved' | 'rejected', notes: string) => {
      const db = getDb();
      const idx = db.pedigreeRequests.findIndex((r: any) => r.id === id);
      if (idx !== -1) {
        db.pedigreeRequests[idx].status = status;
        db.pedigreeRequests[idx].admin_notes = notes;

        // If approved, update pet pedigree count/reg
        if (status === 'approved') {
          const petIdx = db.pets.findIndex((p: any) => p.id === db.pedigreeRequests[idx].pet_id);
          if (petIdx !== -1) {
            db.pets[petIdx].pedigreeReg = db.pedigreeRequests[idx].pedigree_reg;
            db.pets[petIdx].is_pedigree_verified = true;
          }
        }
        saveDb(db);
        return { success: true };
      }
      return { success: false };
    }
  },
  getAdminUsers: async (adminEmail: string, query: string = '') => {
    const db = getDb();
    let filteredUsers = db.users;

    if (query) {
      const q = query.toLowerCase();
      filteredUsers = db.users.filter((u: any) =>
        u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    }

    return {
      users: filteredUsers.map((u: any) => ({
        ...u,
        pet_summary: 'Mascota Mock',
        plan_display: 'Free',
        status: 'Active',
        pet_count: (db.pets || []).filter((p: any) => p.ownerEmail === u.email).length
      }))
    };
  },
  adminCreateUser: async (adminEmail: string, data: any) => {
    const db = getDb();
    if (db.users.some((u: any) => u.email === data.email)) {
      return { error: 'El correo ya está registrado' };
    }
    const newUser = {
      ...data,
      id: Date.now().toString(),
      points: 0,
      wallet_balance: 0,
      imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba'
    };
    db.users.push(newUser);
    saveDb(db);
    return { message: 'EXITO', status: 'Usuario creado exitosamente' };
  },
  updateAdminUser: async (adminEmail: string, userId: string, data: any) => { return { success: true }; },
  deleteAdminUser: async (adminEmail: string, userId: string) => { 
    const db = getDb();
    db.users = db.users.filter((u: any) => String(u.id) !== String(userId));
    saveDb(db);
    return { success: true }; 
  },

  searchUsers: async (query: string) => {
    const db = getDb();
    if (query.length < 3) return { users: [] };
    const users = db.users.filter((u: any) =>
      u.name.toLowerCase().includes(query.toLowerCase()) ||
      u.email.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10);
    return { users };
  },

  // --- ECOSYSTEM MOCK ---
  getBusinesses: async () => {
    const db = getDb();
    return { success: true, businesses: db.businesses || [] };
  },
  addBusiness: async (data: any) => {
    const db = getDb();
    if (!db.businesses) db.businesses = [];
    const newBiz = { ...data, id: Date.now() };
    db.businesses.push(newBiz);
    saveDb(db);
    return { success: true, id: newBiz.id };
  },
  updateBusiness: async (id: number, data: any) => {
    const db = getDb();
    if (!db.businesses) return { success: false };
    const idx = db.businesses.findIndex((b: any) => b.id === id);
    if (idx !== -1) {
      db.businesses[idx] = { ...db.businesses[idx], ...data };
      saveDb(db);
      return { success: true };
    }
    return { success: false };
  },
  deleteBusiness: async (id: number) => {
    const db = getDb();
    if (!db.businesses) return { success: false };
    db.businesses = db.businesses.filter((b: any) => b.id !== id);
    saveDb(db);
    return { success: true };
  },
  // --- MERCADO PAGO ---
  createMPPreference: async (items: any[], payerEmail: string) => {
    return {
      success: true,
      preference_id: 'mock_pref_id',
      init_point: 'https://www.mercadopago.cl',
      sandbox_init_point: 'https://sandbox.mercadopago.cl'
    };
  }
};

// --- REAL API IMPLEMENTATION ---
// OJO: dentro de la app instalada (Capacitor), el WebView vive en un host
// virtual local (no amigopeludo.cl) y el propio backend_php/ viaja empaquetado
// como archivos estáticos dentro del APK (copy_to_dist.js lo copia a dist/).
// Una ruta relativa como "/backend_php/pets.php" ahí adentro no sale a
// internet — Capacitor la resuelve contra ese archivo .php empaquetado y lo
// sirve como texto plano (de ahí el error "Unexpected token '<', '<?php'").
// Por eso, corriendo nativo, se fuerza siempre la URL absoluta real.
export const API_URL = Capacitor.isNativePlatform()
  ? 'https://amigopeludo.cl/backend_php'
  : ((typeof process !== 'undefined' && process.env?.VITE_API_URL) || (import.meta as any).env?.VITE_API_URL || '/backend_php');

// --- HELPER JWT ---
const getAuthHeaders = () => {
  const token = localStorage.getItem('nexus_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// --- HELPERS CRIADERO (breeder.php) ---
const breederRequest = async (method: 'GET' | 'POST', action: string, body: any, asUser?: string | number, extra: Record<string, string> = {}) => {
  let url = `${API_URL}/breeder.php`;
  const init: RequestInit = { method, headers: getAuthHeaders(), cache: 'no-store' };
  if (method === 'GET') {
    const q = new URLSearchParams({ action, _: String(Date.now()), ...extra });
    if (asUser) q.set('asUser', String(asUser));
    url += `?${q.toString()}`;
  } else {
    init.body = JSON.stringify({ action, ...body, ...(asUser ? { asUser } : {}) });
  }
  const res = await fetch(url, init);
  const text = await res.text();
  let json: any = {};
  try { json = JSON.parse(text); } catch { throw new Error('Respuesta inválida del servidor'); }
  if (!res.ok || json.error) {
    const err: any = new Error(json.error || `Error ${res.status}`);
    err.code = json.code; // p. ej. PLAN_LIMIT cuando se topa con el límite del plan Gratis
    throw err;
  }
  return json;
};
// Contratos con firma (breeder_contracts.php): mismas reglas de sesión que breeder.php; las acciones "públicas" van sin token.
const contractRequest = async (method: 'GET' | 'POST', action: string, body: any = {}, opts: { auth?: boolean; asUser?: string | number; extra?: Record<string, string> } = {}) => {
  const auth = opts.auth !== false;
  let url = `${API_URL}/breeder_contracts.php`;
  const headers: Record<string, string> = auth ? (getAuthHeaders() as any) : { 'Content-Type': 'application/json' };
  const init: RequestInit = { method, headers, cache: 'no-store' };
  if (method === 'GET') {
    const q = new URLSearchParams({ action, _: String(Date.now()), ...(opts.extra || {}) });
    if (opts.asUser) q.set('asUser', String(opts.asUser));
    url += `?${q.toString()}`;
  } else {
    init.body = JSON.stringify({ action, ...body, ...(opts.asUser ? { asUser: opts.asUser } : {}) });
  }
  const res = await fetch(url, init);
  const text = await res.text();
  let json: any = {};
  try { json = JSON.parse(text); } catch { throw new Error('Respuesta inválida del servidor'); }
  if (!res.ok || json.error) {
    const err: any = new Error(json.error || `Error ${res.status}`);
    err.code = json.code;
    throw err;
  }
  return json;
};

const breederGet = (action: string, asUser?: string | number, extra: Record<string, string> = {}) => breederRequest('GET', action, null, asUser, extra);
const breederPost = (action: string, body: any = {}, asUser?: string | number) => breederRequest('POST', action, body, asUser);

// --- HELPERS VETERINARIA (vet_plans.php) ---
const vetPlansRequest = async (method: 'GET' | 'POST', action: string, body: any = {}) => {
  let url = `${API_URL}/vet_plans.php`;
  const init: RequestInit = { method, headers: getAuthHeaders(), cache: 'no-store' };
  if (method === 'GET') {
    url += `?${new URLSearchParams({ action, _: String(Date.now()) }).toString()}`;
  } else {
    init.body = JSON.stringify({ action, ...body });
  }
  const res = await fetch(url, init);
  const text = await res.text();
  let json: any = {};
  try { json = JSON.parse(text); } catch { throw new Error('Respuesta inválida del servidor'); }
  if (!res.ok || json.error) throw new Error(json.error || `Error ${res.status}`);
  return json;
};

const apiReal = {
  // --- AUTH ---
  login: async (email: string, password: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await res.text();
        console.error('[login] Respuesta no-JSON del servidor:', res.status, text.slice(0, 300));
        return { error: `Error del servidor (${res.status}). Ver consola para detalles.` };
      }
      return await res.json();
    } catch (e) {
      console.error('[login] Error de red:', e);
      return { error: 'Sin conexión al servidor. Verifica tu internet e intenta de nuevo.' };
    }
  },

  register: async (userData: any) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await res.text();
        console.error('[register] Respuesta no-JSON:', res.status, text.slice(0, 300));
        return { error: `Error del servidor (${res.status})` };
      }
      return await res.json();
    } catch (e) {
      console.error('[register] Error de red:', e);
      return { error: 'Sin conexión al servidor. Verifica tu internet e intenta de nuevo.' };
    }
  },

  adminCreateUser: async (adminEmail: string, userData: any) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=admin_create_user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { error: 'Error de conexión con el servidor' };
    }
  },

  updateProfile: async (userData: any) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=update_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { error: 'Error de conexión con el servidor' };
    }
  },

  changePassword: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=change_password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { success: false, message: 'Error de conexión con el servidor' };
    }
  },

  forgotPassword: async (email: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      // Verificar que la respuesta sea JSON antes de parsearla
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[forgotPassword] Respuesta no-JSON del servidor:', res.status, text.slice(0, 200));
        return { success: false, message: `Error del servidor (${res.status})` };
      }
      return await res.json();
    } catch (e: any) {
      console.error('[forgotPassword] Error de red:', e?.message);
      return { success: false, message: 'Sin conexión al servidor. Verifica tu internet e intenta de nuevo.' };
    }
  },

  resetPassword: async (token: string, password: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { success: false, message: 'Error de conexión' };
    }
  },

  searchUsers: async (query: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=search&q=${encodeURIComponent(query)}`);
      return await res.json();
    } catch (e) {
      console.error(e);
      return { users: [] };
    }
  },

  socialLogin: async (email: string, name: string, provider: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=social_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, provider })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { error: 'Error de red' };
    }
  },

  syncProfile: async (id: string | number) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { error: 'Error de sincronización' };
    }
  },

  updatePassword: async (userId: string | number, newPassword: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=update_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId, new_password: newPassword })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: 'Error de red' };
    }
  },

  // --- PETS ---

  getPets: async (email: string) => {
    try {
      // no-store: evita que el WebView devuelva una lista vieja cacheada cuando
      // una mascota se agregó/editó desde la otra versión de la pantalla (clásica
      // vs. nueva propuesta), que comparten esta misma URL de consulta.
      const res = await fetch(`${API_URL}/pets.php?userId=${encodeURIComponent(email)}`, { cache: 'no-store' });
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  searchPets: async (query: string) => {
    try {
      const res = await fetch(`${API_URL}/pets.php?search=${encodeURIComponent(query)}`);
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  getPetById: async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/pets.php?id=${id}`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  },

  addPet: async (data: any) => {
    const res = await fetch(`${API_URL}/pets.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const response = await res.json();
    if (!res.ok || response.error) {
      throw new Error(response.message || response.error || 'Fallo al guardar en el servidor');
    }
    return { ...data, ...response.pet, message: 'EXITO' };
  },

  updatePet: async (id: string, data: any) => {
    const res = await fetch(`${API_URL}/pets.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Fallo al actualizar en el servidor');
    }
    return { id, ...data };
  },

  deletePet: async (id: string) => {
    await fetch(`${API_URL}/pets.php?id=${id}`, { method: 'DELETE' });
  },

  // Vincula el collar GPS (por su MAC) a una mascota — ver vincular_tracker.php.
  linkTracker: async (petId: string | number, macAddress: string) => {
    const res = await fetch(`${API_URL}/vincular_tracker.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ pet_id: petId, mac_address: macAddress })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.error) {
      throw new Error(data.error || 'No se pudo vincular el collar');
    }
    return data;
  },

  transferPet: async (id: string, newEmail: string) => {
    // TODO: Implement on backend
    console.warn('Transfer Pet not implemented on server yet');
  },

  updatePetLocation: async (id: string, lat: string, lng: string) => {
    try {
      const res = await fetch(`${API_URL}/pets.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng })
      });
      return await res.json();
    } catch {
      return { success: false };
    }
  },
  // --- VET PATIENTS ---
  getVetPatients: async (vetId: string) => {
    try {
      const res = await fetch(`${API_URL}/vet_patients.php?vet_id=${vetId}`);
      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  linkVetPatient: async (vetId: string, petId: string) => {
    const res = await fetch(`${API_URL}/vet_patients.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vet_id: vetId, pet_id: petId })
    });
    return await res.json();
  },

  unlinkVetPatient: async (vetId: string, petId: string) => {
    const res = await fetch(`${API_URL}/vet_patients.php`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vet_id: vetId, pet_id: petId })
    });
    return await res.json();
  },

  // --- VET STAFF ---
  getVetStaff: async (vetId: string) => {
    try {
      const res = await fetch(`${API_URL}/vet_staff.php?vet_id=${vetId}`);
      if (!res.ok) return [];
      return await res.json();
    } catch { return []; }
  },
  addVetStaff: async (data: any) => {
    const res = await fetch(`${API_URL}/vet_staff.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },
  updateVetStaff: async (data: any) => {
    const res = await fetch(`${API_URL}/vet_staff.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },
  deleteVetStaff: async (id: string, vetId: string) => {
    const res = await fetch(`${API_URL}/vet_staff.php`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, vet_id: vetId })
    });
    return await res.json();
  },

  // --- HEALTH RECORDS ---
  getHealthRecords: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/health.php?petId=${petId}&_=${Date.now()}`, { cache: 'no-store' });
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  addHealthRecord: async (data: any) => {
    const res = await fetch(`${API_URL}/health.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await res.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      console.error("Invalid JSON from health.php:", text);
      throw new Error(`Error del servidor (Respuesta no válida): ${text.substring(0, 100)}`);
    }
    if (result.error) throw new Error(result.message || result.error);
    return result;
  },

  updateHealthRecord: async (id: string, data: any) => {
    const res = await fetch(`${API_URL}/health.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  deleteHealthRecord: async (id: string) => {
    await fetch(`${API_URL}/health.php?id=${id}`, { method: 'DELETE' });
  },

  // --- VACCINES ---
  getVaccines: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/vaccines.php?petId=${petId}&_=${Date.now()}`, { cache: 'no-store' });
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  addVaccine: async (data: any) => {
    const res = await fetch(`${API_URL}/vaccines.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (result.error) throw new Error(result.message || result.error);
    return result;
  },

  // --- PET DOCUMENTS ---
  getPetDocuments: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/pet_documents.php?petId=${petId}`);
      return await res.json();
    } catch (e) {
      return [];
    }
  },


  addPetDocument: async (data: any) => {
    const res = await fetch(`${API_URL}/pet_documents.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  deletePetDocument: async (id: number) => {
    const res = await fetch(`${API_URL}/pet_documents.php?id=${id}`, { method: 'DELETE' });
    return await res.json();
  },

  updateVaccine: async (id: string, data: any) => {
    const res = await fetch(`${API_URL}/vaccines.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  deleteVaccine: async (id: string) => {
    await fetch(`${API_URL}/vaccines.php?id=${id}`, { method: 'DELETE' });
  },

  // --- GOOGLE PLAY BILLING ---
  // El celular nunca activa Premium por sí solo: manda el purchaseToken que
  // entrega Play Billing y el servidor lo valida contra la API de Google
  // antes de tocar el plan (ver backend_php/verify_purchase.php).
  verifyPurchase: async (subscriptionId: string, purchaseToken: string) => {
    const res = await fetch(`${API_URL}/verify_purchase.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ subscriptionId, purchaseToken })
    });
    const json = await res.json();
    if (!res.ok || json.success === false) throw new Error(json.error || 'No se pudo verificar la compra');
    return json;
  },

  // --- MEDICATIONS ---
  getMedications: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/medications.php?petId=${petId}&_=${Date.now()}`, { cache: 'no-store' });
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  addMedication: async (data: any) => {
    const res = await fetch(`${API_URL}/medications.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (result.error) throw new Error(result.message || result.error);
    return result;
  },

  updateMedication: async (id: string, data: any) => {
    const res = await fetch(`${API_URL}/medications.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  deleteMedication: async (id: string) => {
    await fetch(`${API_URL}/medications.php?id=${id}`, { method: 'DELETE' });
  },

  getAllMedications: async (email: string) => {
    try {
      const res = await fetch(`${API_URL}/medications.php?action=all&userId=${encodeURIComponent(email)}`);
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  // --- SHELTER MODULE ---
  shelter: {
    getProfile: async (userId: string | number) => {
      const res = await fetch(`${API_URL}/shelter.php?action=get_profile&userId=${userId}`);
      return await res.json();
    },
    updateProfile: async (data: any) => {
      const res = await fetch(`${API_URL}/shelter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_profile', ...data })
      });
      return await res.json();
    },
    getResidents: async (userId?: string | number, shelterId?: number) => {
      const url = shelterId
        ? `${API_URL}/shelter.php?action=get_residents&shelterId=${shelterId}`
        : `${API_URL}/shelter.php?action=get_residents&userId=${userId}`;
      const res = await fetch(url);
      return await res.json();
    },
    getAdoptions: async (userId?: string | number, shelterId?: number) => {
      const url = shelterId
        ? `${API_URL}/shelter.php?action=get_adoptions&shelterId=${shelterId}`
        : `${API_URL}/shelter.php?action=get_adoptions&userId=${userId}`;
      const res = await fetch(url);
      return await res.json();
    },
    updateAdoptionStatus: async (requestId: number, status: string, notes?: string) => {
      const res = await fetch(`${API_URL}/shelter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_adoption_status', requestId, status, notes })
      });
      return await res.json();
    },
    getSponsors: async (userId: string | number) => {
      const res = await fetch(`${API_URL}/shelter.php?action=get_sponsors&userId=${userId}`);
      return await res.json();
    },
    // --- Página pública /adoptame (sin login) ---
    getPublicAdoptable: async (opts?: { limit?: number; offset?: number; species?: string }) => {
      try {
        const params = new URLSearchParams({ action: 'public_adoptable' });
        if (opts?.limit != null) params.set('limit', String(opts.limit));
        if (opts?.offset != null) params.set('offset', String(opts.offset));
        if (opts?.species) params.set('species', opts.species);
        const res = await fetch(`${API_URL}/shelter.php?${params.toString()}`);
        return await res.json();
      } catch (e) {
        return { success: false, pets: [] };
      }
    },
    submitAdoptionInterest: async (data: any) => {
      const res = await fetch(`${API_URL}/shelter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'submit_interest', ...data })
      });
      return await res.json();
    },
    // --- Padrinos (sponsors.php) ---
    /** Público: ¿se puede apadrinar esta mascota? monto mínimo y padrinos activos. */
    getSponsorInfo: async (petId: string | number) => {
      try {
        const res = await fetch(`${API_URL}/sponsors.php?action=pet_info&pet_id=${encodeURIComponent(String(petId))}`);
        return await res.json();
      } catch { return { success: false, enabled: false }; }
    },
    /** Público: postular como padrino (si hay sesión, se asocia a la cuenta). */
    requestSponsorship: async (data: { pet_id: string | number; name: string; email: string; phone?: string; amount: number; message?: string; website?: string }) => {
      const res = await fetch(`${API_URL}/sponsors.php?action=request`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
      return await res.json();
    },
    /** Albergue: padrinos + configuración. */
    listSponsorships: async () => {
      const res = await fetch(`${API_URL}/sponsors.php?action=list`, { headers: getAuthHeaders() });
      return await res.json();
    },
    updateSponsorship: async (data: { id: number; status: string; notes?: string }) => {
      const res = await fetch(`${API_URL}/sponsors.php?action=update`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
      return await res.json();
    },
    saveSponsorSettings: async (data: { enabled: boolean; min_amount: number; instructions: string }) => {
      const res = await fetch(`${API_URL}/sponsors.php?action=settings`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
      return await res.json();
    }
  },

  // --- VET MODULE (solo plan Gratis/Pro/Élite por ahora, mismo esquema que criaderos) ---
  vet: {
    adminPlans: async (): Promise<any[]> => (await vetPlansRequest('GET', 'admin_plans')).vets || [],
    adminSetPlan: async (data: { userId: number; plan: 'free' | 'pro' | 'elite'; days?: number }) => vetPlansRequest('POST', 'admin_set_plan', data),
  },

  // --- BREEDER MODULE ---
  // Todas las llamadas van con el token de sesión; el servidor toma la identidad del
  // token (asUser solo lo respeta si quien llama es root/admin — panel de monitoreo).
  breeder: {
    checkStatus: async (asUser?: string | number) => breederGet('check_status', asUser),
    getManada: async (asUser?: string | number): Promise<any[]> => {
      const res = await breederGet('manada', asUser);
      return (res.pets || []).map((p: any) => apiReal.formatPet(p));
    },
    getInventory: async (asUser?: string | number) => breederGet('inventory', asUser),
    // Página pública /p/<id>: no requiere sesión.
    // `slugOrId`: dirección del criadero (/criadero/<slug>) o, por compatibilidad, el ID de usuario (/p/<id>).
    getPublicProfile: async (slugOrId: string | number) => {
      const res = await fetch(`${API_URL}/breeder.php?action=public_profile&slug=${encodeURIComponent(String(slugOrId))}&_=${Date.now()}`, { cache: 'no-store' });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Criadero no encontrado');
      return { breeder: json.breeder || null, pets: (json.pets || []).map((p: any) => apiReal.formatPet(p)) as any[], litters: (json.litters || []) as any[] };
    },
    joinWaitlist: async (data: { slug: string; name: string; email: string; phone?: string; city?: string; preference?: string; notes?: string; website?: string }) => {
      const res = await fetch(`${API_URL}/breeder.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'join_waitlist', ...data }) });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'No se pudo enviar la solicitud');
      return json;
    },
    getWaitlist: async (asUser?: string | number): Promise<any[]> => (await breederGet('waitlist', asUser)).entries || [],
    updateWaitlist: async (data: { id: number; status: string; internalNotes?: string }, asUser?: string | number) => breederPost('update_waitlist', data, asUser),
    deleteWaitlist: async (id: number, asUser?: string | number) => breederPost('delete_waitlist', { id }, asUser),

    // Contratos (plan Élite)
    listContracts: async (asUser?: string | number): Promise<any[]> => (await contractRequest('GET', 'list', {}, { asUser })).contracts || [],
    getContract: async (id: number, asUser?: string | number) => contractRequest('GET', 'get', {}, { asUser, extra: { id: String(id) } }),
    saveContract: async (data: any, asUser?: string | number) => contractRequest('POST', data.id ? 'update' : 'create', data, { asUser }),
    cancelContract: async (id: number, asUser?: string | number) => contractRequest('POST', 'cancel', { id }, { asUser }),
    signContractAsBreeder: async (data: { id: number; signer: string; signature?: string | null }, asUser?: string | number) => contractRequest('POST', 'sign_breeder', data, { asUser }),
    resendContract: async (id: number, asUser?: string | number) => contractRequest('POST', 'resend', { id }, { asUser }),
    // Públicas (comprador, con el enlace del correo)
    viewContract: async (token: string) => contractRequest('GET', 'view', {}, { auth: false, extra: { token } }),
    requestContractCode: async (token: string) => contractRequest('POST', 'request_otp', { token }, { auth: false }),
    signContractAsBuyer: async (data: { token: string; code: string; signer: string; signature?: string | null; accept: boolean }) => contractRequest('POST', 'sign_buyer', data, { auth: false }),
    // Administración (solo root/admin): planes de todos los criaderos.
    adminPlans: async (): Promise<any[]> => (await breederGet('admin_plans')).breeders || [],
    adminSetPlan: async (data: { userId: number; plan: 'free' | 'pro' | 'elite'; days?: number }) => breederPost('admin_set_plan', data),
    getPlan: async (asUser?: string | number) => breederGet('plan', asUser),
    checkCanAddPet: async (asUser?: string | number) => breederPost('check_can_add_pet', {}, asUser),
    getLitters: async (asUser?: string | number): Promise<any[]> => (await breederGet('litters', asUser)).litters || [],
    getHeats: async (asUser?: string | number): Promise<any[]> => (await breederGet('heats', asUser)).heats || [],
    getTransfers: async (asUser?: string | number): Promise<any[]> => (await breederGet('transfers', asUser)).transfers || [],
    getDocs: async (asUser?: string | number): Promise<{ docs: any[]; validations: any[] }> => {
      const res = await breederGet('docs', asUser);
      return { docs: res.docs || [], validations: res.validations || [] };
    },
    getIncomingTransfers: async (): Promise<any[]> => {
      try { return (await breederGet('incoming')).transfers || []; } catch { return []; }
    },
    getTransferInfo: async (token: string) => breederGet('transfer_info', undefined, { token }),
    acceptTransfer: async (token: string) => breederPost('accept_transfer', { token }),
    rejectTransfer: async (token: string) => breederPost('reject_transfer', { token }),
    createTransfer: async (data: { petId: string | number, toEmail: string }, asUser?: string | number) =>
      breederPost('create_transfer', data, asUser),
    cancelTransfer: async (transferId: number, asUser?: string | number) => breederPost('cancel_transfer', { transferId }, asUser),
    setPetData: async (data: any, asUser?: string | number) => breederPost('set_pet_data', data, asUser),
    saveLitter: async (data: any, asUser?: string | number) => breederPost(data.id ? 'update_litter' : 'create_litter', data, asUser),
    deleteLitter: async (id: string | number, asUser?: string | number) => breederPost('delete_litter', { id }, asUser),
    saveHeat: async (data: any, asUser?: string | number) => breederPost(data.id ? 'update_heat' : 'create_heat', data, asUser),
    deleteHeat: async (id: string | number, asUser?: string | number) => breederPost('delete_heat', { id }, asUser),
    updateProfile: async (data: any, asUser?: string | number) => breederPost('update_profile', data, asUser),

    getValidationRequests: async () => {
      try {
        const res = await fetch(`${API_URL}/breeder_validation.php?action=list`, { headers: getAuthHeaders() });
        const json = await res.json();
        return Array.isArray(json) ? json : [];
      } catch (e) {
        return [];
      }
    },
    submitValidationRequest: async (data: any) => {
      // Las imágenes se optimizan a WebP; un PDF se manda tal cual (antes se renombraba a .webp).
      const isImage = data.document instanceof File && data.document.type.startsWith('image/');
      const doc = isImage ? await convertToWebP(data.document) : data.document;
      const formData = new FormData();
      formData.append('action', 'submit');
      formData.append('pet_id', data.pet_id);
      formData.append('pedigree_reg', data.pedigree_reg);
      if (doc) {
        formData.append('document', doc, isImage ? 'document.webp' : (doc.name || 'document.pdf'));
      }
      const token = localStorage.getItem('nexus_token');
      const res = await fetch(`${API_URL}/breeder_validation.php`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const json = await res.json();
      if (!res.ok || json.success === false) throw new Error(json.error || 'No se pudo enviar la solicitud');
      return json;
    },
    updateValidationStatus: async (id: number, status: 'approved' | 'rejected', notes: string) => {
      const res = await fetch(`${API_URL}/breeder_validation.php?action=update_status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id, status, notes })
      });
      return await res.json();
    }
  },

  // --- UPLOAD ---
  uploadImage: async (file: File) => {
    const optimizedFile = await convertToWebP(file);
    const formData = new FormData();
    formData.append('image', optimizedFile, 'image.webp');
    try {
      const res = await fetch(`${API_URL}/upload.php`, {
        method: 'POST',
        body: formData
      });
      return await res.json();
    } catch (e) {
      return { success: false, message: 'Upload network error' };
    }
  },

  // --- SOCIAL FEED ---
  // limit/offset habilitan scroll infinito en Comunidad — sin esto se
  // descargaban y decodificaban de golpe todas las imágenes del muro,
  // aunque el usuario nunca llegara a verlas todas.
  getPosts: async (opts?: { limit?: number; offset?: number }) => {
    try {
      const params = new URLSearchParams();
      if (opts?.limit != null) params.set('limit', String(opts.limit));
      if (opts?.offset != null) params.set('offset', String(opts.offset));
      const qs = params.toString();
      const res = await fetch(`${API_URL}/posts.php${qs ? `?${qs}` : ''}`);
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  createPost: async (postData: any) => {
    // Con el token el servidor puede comprobar si la cuenta es Premium antes
    // de aceptar los bordes/efectos exclusivos de las cartas.
    const res = await fetch(`${API_URL}/posts.php?action=create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(postData)
    });
    return await res.json();
  },

  getPostById: async (postId: string) => {
    try {
      const res = await fetch(`${API_URL}/posts.php?action=get&id=${postId}`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  },

  likePost: async (postId: string) => {
    const res = await fetch(`${API_URL}/posts.php?action=like&id=${postId}`, { method: 'POST', headers: getAuthHeaders() });
    return await res.json();
  },

  tagPost: async (postId: string, tags: any[]) => {
    await fetch(`${API_URL}/posts.php?action=tag&id=${postId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tags })
    });
  },

  commentPost: async (postId: string, comment: any) => {
    await fetch(`${API_URL}/posts.php?action=comment&id=${postId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment })
    });
  },

  // --- SKILLS ---
  getSkills: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/skills.php?petId=${petId}`);
      return await res.json();
    } catch (e) {
      return [];
    }
  },

  addSkill: async (data: any) => {
    const res = await fetch(`${API_URL}/skills.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },
  // --- CATEGORIES ---
  getCategories: async () => {
    const res = await fetch(`${API_URL}/categories.php`);
    return await res.json();
  },

  // --- GROWTH RECORDS ---
  getGrowthRecords: async (petId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/growth.php?petId=${petId}`);
      if (!res.ok) return [];
      const text = await res.text();
      return JSON.parse(text);
    } catch (e) {
      console.error("Error getGrowthRecords:", e);
      return [];
    }
  },

  addGrowthRecord: async (data: any) => {
    const res = await fetch(`${API_URL}/growth.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await res.text();
    let json;
    try {
      json = JSON.parse(text);
    } catch (e) {
      throw new Error(`Respuesta inválida servidor: ${text.substring(0, 100)}`);
    }
    if (!res.ok || json.error) {
      throw new Error(json.error || json.message || "Error al guardar en BD");
    }
    return json;
  },

  // --- GALLERY ---
  getPetGallery: async (petId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/pet_gallery.php?petId=${petId}`);
      if (!res.ok) return [];
      const text = await res.text();
      return JSON.parse(text);
    } catch (e) {
      console.error("Error getPetGallery:", e);
      return [];
    }
  },

  addPetGalleryImage: async (data: { petId: string | number; url: string }) => {
    const res = await fetch(`${API_URL}/pet_gallery.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { throw new Error('Respuesta inválida servidor'); }
    if (!res.ok || json.error) throw new Error(json.error || "Error al subir imagen a la galería");
    return json;
  },

  deletePetGalleryImage: async (id: string | number) => {
    try {
      const res = await fetch(`${API_URL}/pet_gallery.php?id=${id}`, { method: 'DELETE' });
      return await res.json();
    } catch { return { success: false }; }
  },

  // --- PRODUCTS (E-COMMERCE) ---


  getProducts: async (params: any = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const res = await fetch(`${API_URL}/products.php?${query}`);
      return await res.json();
    } catch (e: any) {
      return { success: false, message: e.message || 'Error de conexión' };
    }
  },

  getProductById: async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/products.php?id=${id}`);
      return await res.json();
    } catch (e) {
      return null;
    }
  },

  createProduct: async (data: any) => {
    const res = await fetch(`${API_URL}/products.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error('Respuesta inválida del servidor: ' + text.substring(0, 100));
    }
  },

  updateProduct: async (data: any) => {
    const res = await fetch(`${API_URL}/products.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error('Respuesta inválida del servidor: ' + text.substring(0, 100));
    }
  },

  deleteProduct: async (id: number) => {
    const res = await fetch(`${API_URL}/products.php?id=${id}`, { method: 'DELETE' });
    return await res.json();
  },

  deleteProducts: async (ids: number[]) => {
    try {
      const res = await fetch(`${API_URL}/products.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, message: e.message };
    }
  },

  // --- REVIEWS ---
  getReviews: async (productId: number) => {
    try {
      const res = await fetch(`${API_URL}/reviews.php?productId=${productId}`);
      const data = await res.json();
      return data.success ? data.data : [];
    } catch {
      return [];
    }
  },

  addReview: async (reviewData: any) => {
    try {
      const res = await fetch(`${API_URL}/reviews.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: 'Connection Error' };
    }
  },

  // --- COUPONS ---
  validateCoupon: async (code: string, subtotal: number, userId?: string) => {
    try {
      const res = await fetch(`${API_URL}/coupons.php?action=validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subtotal, userId })
      });
      return await res.json();
    } catch (e) {
      return { valid: false, message: 'Error de conexión' };
    }
  },

  // --- VET POS ---
  getVetPOSSales: async (params: { date_from?: string; date_to?: string; limit?: number } = {}) => {
    try {
      const q = new URLSearchParams(params as Record<string, string>).toString();
      const res = await fetch(`${API_URL}/vet_pos_sales.php?${q}`);
      return await res.json();
    } catch { return { success: false, data: [], summary: { count: 0, revenue: 0, by_method: {} } }; }
  },

  createVetPOSSale: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_sales.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  updateVetPOSSale: async (id: string, data: any) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_sales.php?id=${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, ...data })
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  initVetTerminalPayment: async (data: { provider: string; amount: number; reference?: string; config: any }) => {
    try {
      const res = await fetch(`${API_URL}/vet_payment_terminal.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  getVetCaja: async (todayOnly = true) => {
    try {
      const q = todayOnly ? `?today=1&date=${new Date().toISOString().split('T')[0]}` : '?limit=30';
      const res = await fetch(`${API_URL}/vet_pos_caja.php${q}`);
      return await res.json();
    } catch { return { success: false, data: null }; }
  },

  openVetCaja: async (data: { start_amount: number; opened_by?: string }) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_caja.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  closeVetCaja: async (id: string, data: any) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_caja.php?id=${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, ...data })
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  // --- ORDERS ---
  getOrders: async () => {
    try {
      const res = await fetch(`${API_URL}/orders.php`);
      return await res.json();
    } catch {
      return { success: false, data: [] };
    }
  },

  deleteOrders: async (ids: number[]) => {
    try {
      const res = await fetch(`${API_URL}/orders.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, message: e.message };
    }
  },

  getAllReviews: async () => {
    try {
      const res = await fetch(`${API_URL}/reviews.php?all=true`);
      const data = await res.json();
      return data.success ? data.data : [];
    } catch { return []; }
  },

  // --- SETTINGS ---
  getPaymentSettings: async () => {
    try {
      const res = await fetch(`${API_URL}/settings.php`);
      return await res.json();
    } catch { return { success: false, data: {} }; }
  },

  savePaymentSettings: async (settings: any) => {
    try {
      const res = await fetch(`${API_URL}/settings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      return await res.json();
    } catch (e: any) { return { success: false, message: e.message }; }
  },

  runWebpayDiagnostic: async () => {
    try {
      const res = await fetch(`${API_URL}/webpay_check.php?format=json`);
      return await res.json();
    } catch (e: any) {
      return { success: false, message: e.message };
    }
  },

  // --- ADMIN TOOLS ---
  generateFakeData: async () => {
    try {
      const res = await fetch(`${API_URL}/generate_fake_data.php`);
      return await res.json();
    } catch { return { success: false, message: 'Connection Error' }; }
  },

  runFullDataAudit: async () => {
    try {
      const res = await fetch(`${API_URL}/diagnose_integrity.php`);
      return await res.json();
    } catch (e: any) {
      return { success: false, results: [{ target: 'NETWORK', status: 'ERROR', message: e.message }] };
    }
  },

  // --- ADMIN PANEL ---
  sendPromoFlash: async () => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=send_promo_flash`);
      return await res.json();
    } catch { return { success: false, message: 'Connection Error' }; }
  },
  getAiDashboardStats: async () => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=ai_dashboard_stats`);
      return await res.json();
    } catch (e) {
      console.error(e);
      return {};
    }
  },

  getAdminStats: async (adminEmail: string) => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=stats&admin_email=${encodeURIComponent(adminEmail)}`);
      return await res.json();
    } catch (e) { return { stats: [], ai_insights: [] }; }
  },

  getAdminUsers: async (adminEmail: string, query: string = '') => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=users&admin_email=${encodeURIComponent(adminEmail)}&q=${encodeURIComponent(query)}`);
      return await res.json();
    } catch (e) { return { users: [] }; }
  },

  updateAdminUser: async (adminEmail: string, userId: string, data: any) => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=update_user&admin_email=${encodeURIComponent(adminEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId, ...data })
      });
      return await res.json();
    } catch (e) { return { error: 'Connection Error' }; }
  },

  deleteAdminUser: async (adminEmail: string, userId: string) => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=delete_user&admin_email=${encodeURIComponent(adminEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId })
      });
      return await res.json();
    } catch (e) { return { error: 'Connection Error' }; }
  },

  getAdminAllPets: async (adminEmail: string) => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=all_pets&admin_email=${encodeURIComponent(adminEmail)}`);
      return await res.json();
    } catch (e) { return { pets: [] }; }
  },

  getAdoptionsList: async (): Promise<{ pets: any[]; stats: { total: number; available: number; adopted: number } }> => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=adoptions_list`, { cache: 'no-store' });
      return await res.json();
    } catch (e) { return { pets: [], stats: { total: 0, available: 0, adopted: 0 } }; }
  },
  setAdoptionStatus: async (petId: number, adopted: boolean) => {
    const res = await fetch(`${API_URL}/admin_api.php?action=set_adoption_status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pet_id: petId, adopted })
    });
    return await res.json();
  },
  updateAdoptionPet: async (petId: number, data: { name?: string; species?: string; breed?: string; gender?: string; adoption_note?: string }) => {
    const res = await fetch(`${API_URL}/admin_api.php?action=update_adoption_pet`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pet_id: petId, ...data })
    });
    return await res.json();
  },

  // --- VIDEOS DE MUESTRA (sección oculta Panel Root) ---
  uploadVideo: async (file: File) => {
    const formData = new FormData();
    formData.append('video', file);
    try {
      const res = await fetch(`${API_URL}/upload_video.php`, { method: 'POST', body: formData });
      return await res.json();
    } catch (e) {
      return { success: false, message: 'Upload network error' };
    }
  },
  getAdminVideos: async () => {
    try {
      const res = await fetch(`${API_URL}/admin_videos.php`);
      return await res.json();
    } catch (e) { return { success: false, videos: [] }; }
  },
  addAdminVideo: async (title: string, url: string) => {
    const res = await fetch(`${API_URL}/admin_videos.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, url })
    });
    return await res.json();
  },
  deleteAdminVideo: async (id: string) => {
    const res = await fetch(`${API_URL}/admin_videos.php?id=${id}`, { method: 'DELETE' });
    return await res.json();
  },

  // --- GAMIFICATION (V2) ---
  getGamificationProfile: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/gamification.php?petId=${petId}`);
      return await res.json();
    } catch { return { profile: null, completed_challenges: [] }; }
  },

  completeChallenge: async (data: any) => {
    const res = await fetch(`${API_URL}/gamification.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  // --- PLAYDATES (SOCIAL) ---
  getPlaydateCandidates: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/playdates.php?pet_id=${petId}`);
      return await res.json();
    } catch { return { success: false, candidates: [] }; }
  },

  sendPlaydateAction: async (initiatorId: string, targetId: string, action: 'like' | 'pass') => {
    const res = await fetch(`${API_URL}/playdates.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ initiator_pet_id: initiatorId, target_pet_id: targetId, action })
    });
    return await res.json();
  },

  // --- QUEDADAS (proponer lugar/fecha/hora para juntarse con un amigo) ---
  getPlaydateMeetings: async () => {
    try {
      const res = await fetch(`${API_URL}/playdate_meetings.php`, { headers: getAuthHeaders() });
      return await res.json();
    } catch { return { success: false, meetings: [] }; }
  },
  proposePlaydateMeeting: async (data: any) => {
    const res = await fetch(`${API_URL}/playdate_meetings.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return await res.json();
  },
  respondPlaydateMeeting: async (id: string | number, status: 'confirmed' | 'cancelled') => {
    const res = await fetch(`${API_URL}/playdate_meetings.php?id=${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status })
    });
    return await res.json();
  },

  // --- SOS & LOST PETS ---
  getActiveAlerts: async () => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php`);
      return await res.json();
    } catch { return { success: false, alerts: [] }; }
  },

  getFilteredAlerts: async (status: string, geoParams: any = {}) => {
    try {
      const queryParams = new URLSearchParams(geoParams).toString();
      const res = await fetch(`${API_URL}/lost_pets.php?status=${status}&${queryParams}`);
      return await res.json();
    } catch { return { success: false, alerts: [] }; }
  },

  createLostPetAlert: async (data: any) => {
    const res = await fetch(`${API_URL}/lost_pets.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return await res.json();
  },
  
  resolveAlert: async (alertId: string | number, messageOrUserId?: string, userId?: string, finderEmail?: string) => {
    // El backend (lost_pets.php) solo resuelve alertas vía PUT con { id }.
    // Antes esto llamaba a POST ?action=resolve, que no existe en el backend:
    // en vez de resolver, terminaba creando una alerta nueva y vacía cada vez
    // que alguien tocaba "¡Ya lo encontré!", y la original nunca cambiaba de
    // estado (por eso nunca pasaba a la sección de recuperados).
    const res = await fetch(`${API_URL}/lost_pets.php`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id: alertId, message: messageOrUserId, finder_email: finderEmail })
    });
    return await res.json();
  },

  getResolvedAlerts: async () => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php?status=resolved`);
      return await res.json();
    } catch { return { success: false, alerts: [] }; }
  },

  deleteAlert: async (alertId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php?id=${alertId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  // Reseñas de dueños que recuperaron a su mascota — se muestran en la web
  // (sección Testimonios) y en la tienda.
  submitTestimonial: async (data: { alert_id?: string | number; pet_name?: string; pet_image?: string; author_name?: string; rating: number; comment: string; source?: string }) => {
    try {
      const res = await fetch(`${API_URL}/testimonials.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  getTestimonials: async (limit = 20) => {
    try {
      const res = await fetch(`${API_URL}/testimonials.php?limit=${limit}`);
      return await res.json();
    } catch { return { success: false, testimonials: [] }; }
  },

  // Solicitud de eliminación de cuenta (requisito de Data Safety de Play).
  requestAccountDeletion: async (data: { email: string; reason?: string }) => {
    try {
      const res = await fetch(`${API_URL}/account_deletion.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  // Reportar/bloquear en Comunidad — seguridad básica exigida por Google
  // Play para apps con contenido generado por usuarios.
  reportPost: async (postId: string | number, reason: string, details?: string) => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=report`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ post_id: postId, reason, details })
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },
  blockUser: async (blockedId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=block`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ blocked_id: blockedId })
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },
  unblockUser: async (blockedId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=unblock`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ blocked_id: blockedId })
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },
  getBlockedUsers: async () => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=blocked_list`, { headers: getAuthHeaders() });
      return await res.json();
    } catch { return { success: false, blocked: [] }; }
  },
  getCommunityReports: async () => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=reports`, { headers: getAuthHeaders() });
      return await res.json();
    } catch { return { success: false, reports: [] }; }
  },
  resolveCommunityReport: async (reportId: string | number, decision: 'dismissed' | 'removed') => {
    try {
      const res = await fetch(`${API_URL}/community_safety.php?action=resolve_report`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ report_id: reportId, decision })
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  // Limpieza de Comunidad (solo root) — borra cualquier post, incluyendo
  // tarjetas de alerta SOS que quedaron pegadas de reportes de prueba viejos.
  deletePost: async (postId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/posts.php?id=${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  // Panel admin (Historial de reportes) — editar nombre/especie/raza/estado
  // de cualquier reporte, tenga o no cuenta asociada. Root-only en backend.
  adminUpdateAlert: async (alertId: string | number, data: { pet_name?: string; species?: string; breed?: string; status?: string }) => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php?action=admin_update`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: alertId, ...data })
      });
      return await res.json();
    } catch { return { success: false, error: 'Error de conexión' }; }
  },

  getAlertStats: async () => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php?action=stats`);
      return await res.json();
    } catch { return { success: false, count: 0 }; }
  },

  createSighting: async (data: any) => {
    const res = await fetch(`${API_URL}/lost_pets.php?action=create_sighting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  getSightings: async (alertId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/lost_pets.php?action=get_sightings&alert_id=${alertId}`);
      return await res.json();
    } catch { return { success: false, sightings: [] }; }
  },

  getNotifications: async () => {
    try {
      const res = await fetch(`${API_URL}/notifications.php`, { headers: getAuthHeaders() });
      return await res.json();
    } catch { return { success: false, notifications: [] }; }
  },
  markNotificationRead: async (id?: string | number) => {
    try {
      const res = await fetch(`${API_URL}/notifications.php`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  getAdoptionInbox: async () => {
    try {
      const res = await fetch(`${API_URL}/adoption_inbox.php`, { headers: getAuthHeaders() });
      return await res.json();
    } catch { return { success: false, requests: [] }; }
  },
  updateAdoptionRequest: async (id: string, status: 'pending' | 'reviewing' | 'approved' | 'rejected') => {
    try {
      const res = await fetch(`${API_URL}/adoption_inbox.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id, status })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  // --- MEMORIES (V2) ---
  getMemories: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/memories.php?petId=${petId}`);
      return await res.json();
    } catch { return []; }
  },

  addMemory: async (data: any) => {
    const res = await fetch(`${API_URL}/memories.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  // --- ECOSYSTEM (V4) ---
  // --- ECOSYSTEM (V4) ---
  getBusinesses: async (type?: string) => {
    try {
      const url = type ? `${API_URL}/businesses.php?type=${type}` : `${API_URL}/businesses.php`;

      // Parallel fetch: Backend + CSV (Client-side fallback/augmentation)
      const [res, csvPlaces] = await Promise.all([
        fetch(url).then(r => r.json()).catch(() => ({ success: false, businesses: [] })),
        import('./csvLoader').then(mod => mod.loadPlacesFromCSV())
      ]);

      const backend = res && res.businesses ? res.businesses : [];

      // Combine. Note: CSV places use string IDs "csv-N". Backend uses numeric IDs.
      // Manually filter CSV if needed because loadPlacesFromCSV returns all types
      let extra: any[] = csvPlaces || [];
      if (type) {
        extra = extra.filter((p: any) => type === 'vet' ? p.type === 'vet' : type === 'store' ? p.type === 'store' : type === 'park' ? p.type === 'park' : true);
      }

      return {
        success: true,
        businesses: [...backend, ...extra]
      };
    } catch { return { success: false, businesses: [] }; }
  },

  addBusiness: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/businesses.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  updateBusiness: async (id: number, data: any) => {
    try {
      const res = await fetch(`${API_URL}/businesses.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  deleteBusiness: async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/businesses.php?id=${id}`, { method: 'DELETE' });
      return await res.json();
    } catch { return { success: false }; }
  },

  getFriends: async (userId: string) => {
    try {
      const res = await fetch(`${API_URL}/friends.php?user_id=${userId}`);
      return await res.json();
    } catch { return { success: false, friends: [] }; }
  },

  lookupUser: async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/friends.php?action=lookup&id=${encodeURIComponent(id)}`);
      return await res.json();
    } catch { return { success: false }; }
  },

  removeFriend: async (userId: string, friendId: string) => {
    try {
      const res = await fetch(`${API_URL}/friends.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, friend_id: friendId })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  addFriend: async (userId: string, friendEmail?: string, friendId?: string) => {
    try {
      const res = await fetch(`${API_URL}/friends.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, friend_email: friendEmail, friend_id: friendId })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  getWalletBalance: async (userId: string) => {
    try {
      const res = await fetch(`${API_URL}/wallet.php?userId=${userId}`);
      return await res.json();
    } catch { return { points: 0, cash_equivalent: 0 }; }
  },

  redeemPoints: async (userId: string, points: number) => {
    return { success: true };
  },

  getSubscriptions: async (userId: string) => {
    try {
      const res = await fetch(`${API_URL}/subscriptions.php?userId=${userId}`);
      return await res.json();
    } catch { return { success: false, data: [] }; }
  },

  createSubscription: async (data: any) => {
    const res = await fetch(`${API_URL}/subscriptions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  },

  chatWithVet: async (userId: string, message: string, petId?: string, consultationId?: number) => {
    try {
      const res = await fetch(`${API_URL}/ai_vet.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, message, pet_id: petId, consultation_id: consultationId })
      });
      return await res.json();
    } catch { return { reply: 'Error de conexión con el Veterinario Virtual.' }; }
  },

  savePushToken: async (userId: string, token: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=update_fcm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, fcm_token: token })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  sendTestPush: async () => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=send_test_push`);
      return await res.json();
    } catch { return { success: false }; }
  },

  updateUserLocation: async (userId: string, lat: number, lng: number) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=update_location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, lat, lng })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  getGeofences: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/geofences.php?petId=${petId}`);
      return await res.json();
    } catch { return { success: false, geofences: [] }; }
  },

  addGeofence: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/geofences.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  deleteGeofence: async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/geofences.php?id=${id}`, { method: 'DELETE' });
      return await res.json();
    } catch { return { success: false }; }
  },

  getLocationHistory: async (petId: string) => {
    try {
      const res = await fetch(`${API_URL}/location_history.php?pet_id=${petId}`);
      return await res.json();
    } catch { return { success: false, history: [] }; }
  },

  // --- PARTNER REQUESTS ---
  getPartnerRequests: async () => {
    try {
      const res = await fetch(`${API_URL}/partner_requests.php`, { headers: getAuthHeaders(), cache: 'no-store' });
      return await res.json();
    } catch { return { success: false, requests: [] }; }
  },

  createPartnerRequest: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/partner_requests.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', ...data })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  approvePartnerRequest: async (id: string | number, lat?: string, lng?: string, address?: string, city?: string, region?: string, country?: string) => {
    try {
      const res = await fetch(`${API_URL}/partner_requests.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ action: 'approve', id, lat, lng, address, city, region, country })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  deletePartnerRequest: async (id: string | number) => {
    try {
      const res = await fetch(`${API_URL}/partner_requests.php?id=${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  rejectPartnerRequest: async (id: string | number, reason?: string) => {
    try {
      const res = await fetch(`${API_URL}/partner_requests.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ action: 'reject', id, reason: reason || '' })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  resetPartnerPassword: async (email: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=reset_partner_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      return await res.json();
    } catch { return { success: false, message: 'Error de red' }; }
  },

  monitorUser: async (email: string) => {
    try {
      const res = await fetch(`${API_URL}/admin_api.php?action=impersonate&email=${encodeURIComponent(email)}`);
      return await res.json();
    } catch { return { success: false }; }
  },

  // --- GEOCODING (OSM Nominatim) ---
  geocodeAddress: async (address: string) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) {
        return { lat: data[0].lat, lng: data[0].lon, success: true };
      }
      return { success: false };
    } catch (e) {
      console.error("Geocoding error", e);
      return { success: false };
    }
  },

  reverseGeocode: async (lat: number, lng: number) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      if (data && data.address) {
        return {
          address: data.display_name,
          street: data.address.road || '',
          city: data.address.city || data.address.town || data.address.village || '',
          region: data.address.state || '',
          country: data.address.country || '',
          success: true
        };
      }
      return { success: false };
    } catch (e) {
      console.error("Reverse geocoding error", e);
      return { success: false };
    }
  },

  // --- PROXIMITY (VINCULO MAGICO) ---
  broadcastProximity: async (userId: string, lat: number, lng: number) => {
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('lat', lat.toString());
      formData.append('lng', lng.toString());
      const res = await fetch(`${API_URL}/proximity.php?action=broadcast`, {
        method: 'POST',
        body: formData
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  pollProximity: async (userId: string, lat: number, lng: number) => {
    try {
      const res = await fetch(`${API_URL}/proximity.php?action=poll&user_id=${userId}&lat=${lat}&lng=${lng}`);
      return await res.json();
    } catch { return { success: false }; }
  },

  clearProximity: async (userId: string) => {
    try {
      await fetch(`${API_URL}/proximity.php?action=clear&user_id=${userId}`);
    } catch { }
  },

  // --- MERCADO PAGO ---
  createMPPreference: async (items: any[], payerEmail: string, externalReference?: string) => {
    try {
      const res = await fetch(`${API_URL}/mp_create_preference.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, payer_email: payerEmail, external_reference: externalReference })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { success: false, error: 'Error al iniciar pago' };
    }
  },

  // --- PAYPAL (tarjeta internacional, países que Mercado Pago no cubre) ---
  createPaypalOrder: async (amount: number, currency: string, description: string, externalReference?: string, returnUrl?: string, cancelUrl?: string) => {
    try {
      const res = await fetch(`${API_URL}/paypal_create_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount.toFixed(2), currency, description, external_reference: externalReference,
          return_url: returnUrl, cancel_url: cancelUrl,
        })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { success: false, error: 'Error al iniciar pago con PayPal' };
    }
  },

  capturePaypalOrder: async (orderId: string) => {
    try {
      const res = await fetch(`${API_URL}/paypal_capture_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId })
      });
      return await res.json();
    } catch (e) {
      console.error(e);
      return { success: false, error: 'Error al confirmar el pago con PayPal' };
    }
  },

  // Activa Premium tras un pago con PayPal — el servidor vuelve a validar la
  // orden contra PayPal antes de tocar el plan (ver paypal_activate_premium.php).
  activatePaypalPremium: async (orderId: string, cycle: 'monthly' | 'yearly') => {
    const res = await fetch(`${API_URL}/paypal_activate_premium.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ order_id: orderId, cycle })
    });
    const json = await res.json();
    if (!res.ok || json.success === false) throw new Error(json.error || 'No se pudo activar Premium');
    return json;
  },

  // --- ADMIN EMAIL TESTING ---
  previewEmail: async (action: 'preview' | 'send_test', type: string, emailTo?: string) => {
    try {
      const res = await fetch(`${API_URL}/email_templates_api.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, type, email_to: emailTo })
      });
      return await res.json();
    } catch { return { success: false }; }
  },

  sendTestEmail: async (data: { to: string; subject: string; html: string }) => {
    try {
      const res = await fetch(`${API_URL}/email_templates_api.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send_custom', ...data })
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await res.text();
        console.error('[sendTestEmail] no-JSON:', res.status, text.slice(0, 200));
        return { success: false, message: `Error del servidor (${res.status})` };
      }
      return await res.json();
    } catch (e: any) {
      console.error('[sendTestEmail] red:', e?.message);
      return { success: false, message: 'Sin conexión' };
    }
  },

  // --- HELPERS ---
  formatPet: (p: any) => {
    if (!p) return null;
    const img = p.imageUrl || p.image_url || p.image;
    let finalUrl = img;

    if (img) {
      // Handle broken URLs from upload_image.php (undefined $protocol/$host → ":/uploads/...")
      if (typeof img === 'string' && img.match(/^:\/+/)) {
        finalUrl = img.replace(/^:\/+/, '/');
      }

      // 1. Clean Localhost / Local Paths (often found in migrated DBs)
      if (img.includes('localhost') || img.includes('127.0.0.1') || img.includes('://192.168.') || img.includes('C:\\')) {
        // Mock logic using standard headers if env not available
        const apiKey = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MAP_API_KEY) || 'MOCK_KEY';
        const parts = img.split(/[\\/]/);
        const filename = parts[parts.length - 1];
        finalUrl = `/uploads/${filename}`;
      }

      // 2. Handle Relative Paths
      if (finalUrl && !finalUrl.startsWith('http') && !finalUrl.startsWith('data:')) {
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const cleanPath = finalUrl.startsWith('/') ? finalUrl : `/${finalUrl}`;
        const rootUrl = baseUrl.replace('/backend_php', '');
        finalUrl = `${rootUrl}${cleanPath}`;
      }
    }

    // Safe JSON Parsing for complex objects
    const safeParse = (data: any) => {
      if (!data) return null;
      if (typeof data === 'object') return data;
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    };

    return {
      ...p,
      imageUrl: finalUrl || null,
      image_url: finalUrl || null,
      pregnancy_data: safeParse(p.pregnancy_data),
      reproductive_history: safeParse(p.reproductive_history),
      offspring_data: safeParse(p.offspring_data),
      friends_data: safeParse(p.friends_data)
    };
  },

  formatUser: (u: any) => {
    if (!u) return null;
    const img = u.imageUrl || u.image_url;
    let finalUrl = img;

    if (img) {
      if (img.includes('localhost') || img.includes('127.0.0.1') || img.includes('://192.168.') || img.includes('C:\\')) {
        const parts = img.split(/[\\/]/);
        finalUrl = `/uploads/${parts[parts.length - 1]}`;
      }

      if (finalUrl && !finalUrl.startsWith('http') && !finalUrl.startsWith('data:')) {
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const cleanPath = finalUrl.startsWith('/') ? finalUrl : `/${finalUrl}`;
        const rootUrl = baseUrl.replace('/backend_php', '');
        finalUrl = `${rootUrl}${cleanPath}`;
      }
    }

    const fallback = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba';
    return {
      ...u,
      imageUrl: finalUrl || fallback,
      image_url: finalUrl || fallback
    };
  },

  formatPost: (post: any) => {
    if (!post) return null;
    let formatted = { ...post };

    const formatImg = (url: string | null) => {
      if (!url) return null;
      let final = url;
      // Handle broken URLs from upload_image.php (undefined $protocol/$host → ":/uploads/...")
      if (final.match(/^:\/+/)) {
        final = final.replace(/^:\/+/, '/');
      }
      if (url.includes('localhost') || url.includes('127.0.0.1') || url.includes('://192.168.') || url.includes('C:\\')) {
        const parts = url.split(/[\\/]/);
        final = `/uploads/${parts[parts.length - 1]}`;
      }
      if (final && !final.startsWith('http') && !final.startsWith('data:')) {
        const baseUrl = API_URL.replace('/backend_php', '');
        return `${baseUrl}${final.startsWith('/') ? '' : '/'}${final}`;
      }
      return final;
    };

    if (post.image_url) formatted.image_url = formatImg(post.image_url);
    if (post.pet_image) formatted.pet_image = formatImg(post.pet_image);

    return formatted;
  },

  // Appointments — todas las llamadas con sesión llevan el token (appointments.php verifica quién llama).
  createAppointment: async (data: any) => {
    const res = await fetch(`${API_URL}/appointments.php?action=create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  getVetAppointments: async (vetId: string | number) => {
    const res = await fetch(`${API_URL}/appointments.php?action=get_for_vet&vet_id=${vetId}`, { headers: getAuthHeaders() });
    return res.json();
  },
  getUserAppointments: async (userId: string | number) => {
    const res = await fetch(`${API_URL}/appointments.php?action=get_for_user&user_id=${userId}`, { headers: getAuthHeaders() });
    return res.json();
  },
  updateAppointmentStatus: async (data: { id: number, status: string }) => {
    const res = await fetch(`${API_URL}/appointments.php?action=update_status`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  /** Clínicas que reciben reservas (veterinarias aprobadas en Amigo Peludo). */
  getBookableVets: async (): Promise<any[]> => {
    try {
      const res = await fetch(`${API_URL}/appointments.php?action=bookable_vets`);
      const json = await res.json();
      return json?.success && Array.isArray(json.vets) ? json.vets : [];
    } catch { return []; }
  },
  /** Horas ya tomadas de una clínica en un día (HH:MM). */
  getVetTakenSlots: async (vetId: string | number, date: string): Promise<string[]> => {
    try {
      const res = await fetch(`${API_URL}/appointments.php?action=availability&vet_id=${encodeURIComponent(String(vetId))}&date=${date}`);
      const json = await res.json();
      return json?.success && Array.isArray(json.taken) ? json.taken : [];
    } catch { return []; }
  },

  // Export conditional - Force API Real for now
  API_URL: API_URL
};

const wrapPets = (data: any) => {
  if (Array.isArray(data)) return data.map(apiReal.formatPet);
  if (data && typeof data === 'object') return apiReal.formatPet(data);
  return data;
};

// Re-export but wrapping results
export const api = {
  ...apiReal,
  getPets: async (email: string) => wrapPets(await apiReal.getPets(email)),
  searchPets: async (query: string) => wrapPets(await apiReal.searchPets(query)),
  getPetById: async (id: string) => wrapPets(await apiReal.getPetById(id)),
  getVetPatients: async (vetId: string) => wrapPets(await apiReal.getVetPatients(vetId)),
  addPet: async (data: any) => {
    const res = await apiReal.addPet(data);
    if (res && res.pet) res.pet = apiReal.formatPet(res.pet);
    return { ...res, success: true };
  },
  updatePet: async (id: string, data: any) => wrapPets(await apiReal.updatePet(id, data)),
  linkTracker: async (petId: string | number, macAddress: string) => apiReal.linkTracker(petId, macAddress),
  getAdminAllPets: async (adminEmail: string) => {
    const res = await apiReal.getAdminAllPets(adminEmail);
    if (res && res.pets) res.pets = wrapPets(res.pets);
    return res;
  },
  getPosts: async (opts?: { limit?: number; offset?: number }) => {
    const posts = await apiReal.getPosts(opts);
    return Array.isArray(posts) ? posts.map(apiReal.formatPost) : [];
  },
  createPost: async (data: any) => {
    const res = await apiReal.createPost(data);
    return apiReal.formatPost(res);
  },
  socialLogin: async (email: string, name: string, provider: string) => {
    const res = await apiReal.socialLogin(email, name, provider);
    if (res && res.user) res.user = apiReal.formatUser(res.user);
    return res;
  },
  login: async (email: string, password: string) => {
    const res = await apiReal.login(email, password);
    if (res && res.user) res.user = apiReal.formatUser(res.user);
    return res;
  },
  register: async (data: any) => {
    const res = await apiReal.register(data);
    if (res && res.user) res.user = apiReal.formatUser(res.user);
    return res;
  },
  updateProfile: async (data: any) => {
    const res = await apiReal.updateProfile(data);
    if (res && res.user) res.user = apiReal.formatUser(res.user);
    return res;
  },
  syncProfile: async (id: string | number) => {
    const res = await apiReal.syncProfile(id);
    if (res && res.success && res.user) res.user = apiReal.formatUser(res.user);
    return res;
  },
  searchUsers: async (query: string) => await apiReal.searchUsers(query),
  shelter: {
    ...apiReal.shelter,
    getResidents: async (userId?: string | number, shelterId?: number) => {
      const res = await apiReal.shelter.getResidents(userId, shelterId);
      if (res && res.residents) res.residents = wrapPets(res.residents);
      return res;
    }
  },
  breeder: {
    ...apiReal.breeder,
  },
  vet: {
    ...apiReal.vet,
  },
  getBusinesses: apiReal.getBusinesses,
  createAppointment: apiReal.createAppointment,
  getVetAppointments: apiReal.getVetAppointments,
  getUserAppointments: apiReal.getUserAppointments,
  updateAppointmentStatus: apiReal.updateAppointmentStatus,
  getActiveAlerts: apiReal.getActiveAlerts,
  createLostPetAlert: apiReal.createLostPetAlert,
  resolveAlert: apiReal.resolveAlert,
  getResolvedAlerts: apiReal.getResolvedAlerts,
  getAlertStats: apiReal.getAlertStats,
  createSighting: apiReal.createSighting,
  getSightings: apiReal.getSightings,
  runDiagnosis: async () => {
    try {
      const res = await fetch(`${API_URL}/diagnose_links.php`);
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  getPetDocuments: apiReal.getPetDocuments,
  addPetDocument: apiReal.addPetDocument,
  deletePetDocument: apiReal.deletePetDocument,
  getQrTags: async () => {
    try {
      const res = await fetch(`${API_URL}/qr_manager.php`);
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  generateQrTags: async (count: number, customCode?: string) => {
    try {
      const res = await fetch(`${API_URL}/qr_manager.php?action=generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count, custom_code: customCode })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  linkQrTag: async (qrCode: string, petId: string | number) => {
    try {
      const res = await fetch(`${API_URL}/qr_manager.php?action=link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode, pet_id: petId })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  getHospitalizations: async (vetId: string | number, history = false) => {
    try {
      const action = history ? 'history' : 'list';
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=${action}&vet_id=${vetId}`);
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  admitPatient: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=admit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  addHospitalizationTreatment: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=add_treatment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  updateHospitalizationTreatment: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=update_treatment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  logHospitalizationAction: async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=log_action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  markHospitalizationBilled: async (logIds: number[]) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=mark_billed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ log_ids: logIds })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  dischargePatient: async (hospitalizationId: number, notes: string) => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=discharge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hospitalization_id: hospitalizationId, notes })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
  updateHospitalizationGravity: async (hospitalizationId: number, gravity: 'low' | 'medium' | 'critical') => {
    try {
      const res = await fetch(`${API_URL}/hospitalization_api.php?action=update_gravity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hospitalization_id: hospitalizationId, gravity })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },

  // --- POS PRODUCTS ---
  getPOSProducts: async (vetId: string | number, category?: string, search?: string) => {
    try {
      const params = new URLSearchParams({ vet_id: String(vetId) });
      if (category) params.set('category', category);
      if (search)   params.set('search', search);
      const res = await fetch(`${API_URL}/vet_pos_products.php?${params}`);
      return await res.json();
    } catch (e: any) {
      return { success: false, products: [], error: e.message };
    }
  },

  addPOSProduct: async (vetId: string | number, product: {
    name: string; category: string; price: number; unit: string;
    sku?: string; stock_quantity: number; stock_alert_threshold: number;
  }) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_products.php?action=add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vet_id: vetId, ...product })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },

  updatePOSProduct: async (product: {
    id: number; name: string; category: string; price: number; unit: string;
    sku?: string; stock_quantity: number; stock_alert_threshold: number;
  }) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_products.php?action=update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(product)
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },

  deletePOSProduct: async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_products.php?action=delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },

  reducePOSProductStock: async (productId: number, qty: number = 1) => {
    try {
      const res = await fetch(`${API_URL}/vet_pos_products.php?action=reduce_stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, qty })
      });
      return await res.json();
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  },
};
